import { refundsServices } from "@services/apis/Refunds";
import {
  GetAllRefundsData,
  GetAllRefundsResponse,
} from "@services/models/Refund/GetAllRefundsModel";
import ConstantFunctions from "../../utils/constants_functions";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import RefundDetails from "./RefundDetails";

const RefundManagement = () => {
  const constantFunctions = new ConstantFunctions();
  const [refunds, setRefunds] = useState<GetAllRefundsData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [refundId, setRefundId] = useState<string | undefined>(undefined);

  const fetchRefundsData = async () => {
    try {
      setLoading(true);
      const response: GetAllRefundsResponse =
        await refundsServices.getAllRefunds();
      setRefunds(response.data);
    } catch (err: any) {
      setError("Failed to fetch cashier data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRefundsData();
  }, []);

  return (
    <>
      <div className="h-[70px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 pl-[20px]">
        <p className="flex text-text-base text-[14px] font-semibold items-center">
          Refund Management
        </p>
      </div>
      <div className="flex flex-row items-start">
        <div
          className="flex flex-col bg-bg-page-body py-[20px] px-[12px] overflow-hidden w-3/4"
          style={{ height: "calc(100dvh - 70px)" }}
        >
          <div
            className="border border-border-subtle rounded-lg overflow-hidden"
            style={{ height: "100%" }}
          >
            {/* Table Header */}
            <div
              className="bg-bg-base flex flex-row px-[16px] py-[8px] sticky top-0 z-10"
              style={{ boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}
            >
              <div className="font-semibold text-[10px] text-gray-700 w-[15%]">
                Trade Date
              </div>
              <div className="font-semibold text-[10px] text-gray-700 w-[15%]">
                Refund Date
              </div>
              <div className="font-semibold text-[10px] text-gray-700 w-[10%]">
                Trade ID
              </div>
              <div className="font-semibold text-[10px] text-gray-700 w-[15%]">
                Amount
              </div>
              <div className="font-semibold text-[10px] text-gray-700 w-[20%]">
                MOID
              </div>
              <div className="font-semibold text-[10px] text-gray-700 w-[15%]">
                Cx UTR
              </div>
              <div className="font-semibold text-[10px] text-gray-700 w-[15%]">
                UPI ID
              </div>
            </div>

            {/* Table Body */}
            <div
              className="flex flex-col overflow-y-auto"
              style={{ maxHeight: "calc(100% - 40px)" }}
            >
              {refunds.length > 0 &&
                refunds.map((refund, index) => (
                  <div
                    key={index}
                    className={`flex flex-row px-[16px] py-[12px] ${
                      refundId !== refund.refundId.toString()
                        ? "border-b border-border-table"
                        : "border-y border-text-navbar-active"
                    }  text-[10px] text-text-base font-medium cursor-pointer`}
                    onClick={() => setRefundId(refund.refundId.toString())}
                  >
                    <div className="w-[15%]">
                      {constantFunctions.dateFormatInDDMMYY(refund.tradeTime)}
                    </div>
                    <div className="w-[15%]">
                      {constantFunctions.dateFormatInDDMMYY(
                        refund.refundRequestTime
                      )}
                    </div>
                    <div className="w-[10%] flex items-center">
                      <span className="mr-1">{refund.tradeId}</span>
                      <span
                        className={`text-[10px] px-[6px] py-[1px] rounded ${
                          refund.tradeType === "B"
                            ? "bg-bg-status-green"
                            : "bg-bg-status-red"
                        } text-text-base`}
                      >
                        {refund.tradeType}
                      </span>
                    </div>
                    <div className="w-[15%] flex items-center">
                      <div className="flex items-center">
                        <img
                          src={refund.cryptoCurrencyImage}
                          alt=""
                          className="h-[12px] w-[12px] mr-1"
                        />
                        <span>{refund.cryptoAmount?.toFixed(2)}</span>
                      </div>
                      <span className="mx-1">|</span>
                      <div className="flex items-center">
                        <img
                          src={refund.fiatCurrencyImage}
                          alt=""
                          className="h-[12px] w-[12px] mr-1"
                        />
                        <span>{refund.fiatAmount?.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="w-[20%] truncate">{refund.MOID}</div>
                    <div className="w-[15%] truncate">{refund.UTR}</div>
                    <div className="w-[15%] truncate">{refund.upiId}</div>
                  </div>
                ))}
              {loading && (
                <>
                  {[...Array(5)].map((_, index) => (
                    <div
                      key={`loading-${index}`}
                      className="flex flex-row px-[16px] py-[12px] border-b border-border-table"
                    >
                      {[...Array(7)].map((_, i) => (
                        <div
                          key={i}
                          className={i === 6 ? "w-[10%]" : "w-[15%]"}
                        >
                          <Skeleton height={15} />
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              )}
              {!loading && refunds.length === 0 && (
                <div className="text-center py-4 text-[10px] text-gray-500">
                  No refund request found
                </div>
              )}
              {error && (
                <div className="text-center py-4 text-[10px] text-text-status-red">
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>
        {refundId && <RefundDetails refundId={refundId} width="w-1/4"/>}
      </div>
    </>
  );
};

export default RefundManagement;
